import { getFulfillmentHistory } from '@shared/api/fulfillment-history'
import { logger } from '@shared/lib/logger'
import { useAppData } from '@app/providers'
import { useIdentity } from '@vp/ubik-context'
import { useCallback, useRef, useState } from 'react'
import type { Order } from '@shared/types'

export const useFulfillmentHistory = () => {
  const { auth } = useIdentity()
  const { setFulfillmentData, bopApiServiceUrl } = useAppData()
  const [isLoading, setIsLoading] = useState(false)
  const fetchedOrdersRef = useRef<Set<string>>(new Set())

  const prefetch = useCallback(
    async (orders: Order[]) => {
      const token = auth?.getAuthorizationHeader?.()
      if (!token) {
        logger.warn('Fulfillment history prefetch skipped: no auth token', {
          action: 'fulfillment_history_prefetch_skipped',
          component: 'useFulfillmentHistory',
        })
        return
      }

      const unfetched = orders.filter((o) => !fetchedOrdersRef.current.has(o.orderNumber))
      if (unfetched.length === 0) return

      setIsLoading(true)

      const results = await Promise.all(
        unfetched.map((order) =>
          getFulfillmentHistory({
            api: bopApiServiceUrl,
            authorizationHeader: token,
            shopperId: order.orderData.shopperId,
            orderNumber: order.orderNumber,
          }),
        ),
      )

      setFulfillmentData((prev) => {
        const next = new Map(prev)
        results.forEach((map) => {
          map.forEach((fulfillments, lineItemId) => {
            next.set(lineItemId, fulfillments)
          })
        })
        return next
      })
      unfetched.forEach((o) => fetchedOrdersRef.current.add(o.orderNumber))

      setIsLoading(false)
    },
    [auth, setFulfillmentData, bopApiServiceUrl],
  )

  return { isLoading, prefetch }
}
